import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import AccountBoxIconMui from "@material-ui/icons/AccountBox";
import AllInboxIconMui from "@material-ui/icons/AllInbox";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CategoryIconMui from "@material-ui/icons/Category";
import DashboardRoundedIcon from "@material-ui/icons/DashboardRounded";
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import FindInPageOutlinedIcon from "@material-ui/icons/FindInPageOutlined";
import FormatListNumberedRtlIcon from "@material-ui/icons/FormatListNumberedRtl";
import InsertChartIcon from "@material-ui/icons/InsertChart";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import ListOutlinedIcon from "@material-ui/icons/ListOutlined";
import LiveHelpOutlinedIcon from "@material-ui/icons/LiveHelpOutlined";
import LocalShippingIconMui from "@material-ui/icons/LocalShipping";
import LockOpenOutlinedIcon from "@material-ui/icons/LockOpenOutlined";
import MoreOutlinedIcon from "@material-ui/icons/MoreOutlined";
import NotificationsOutlinedIcon from "@material-ui/icons/NotificationsOutlined";
import PublishIconMui from "@material-ui/icons/Publish";
import QueueIconMui from "@material-ui/icons/Queue";
import ReceiptIconMui from "@material-ui/icons/ReceiptRounded";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import ShopOutlinedIcon from "@material-ui/icons/ShopOutlined";
import StarsIconMui from "@material-ui/icons/Stars";
import StyleIconMui from "@material-ui/icons/Style";
import TheatersIconMui from "@material-ui/icons/Theaters";
import ViewComfyIcon from "@material-ui/icons/ViewComfy";
import ViewCompactOutlinedIcon from "@material-ui/icons/ViewCompactOutlined";
import { format } from "date-fns";
import {
  EventIcon,
  ListAltIcon,
  LocalShippingIcon,
  PaymentsIcon,
  SettingsIcon,
  SpaceDashboardIcon,
  StorefrontIcon,
  TrendingUpIcon,
} from "../themeV2";
import { Icons } from "../theme";
import { featureFlags } from "../util";
import { CheckmarkIconImg } from "./iconComponents";

/**
 * Section identifiers for grouping navigation links.
 */
export const SECTION = Object.freeze({
  SHOP_DASHBOARD: "shop-dashboard",
  USER_ACCOUNT: "user-account",
  ADMIN_ORDERS: "admin-orders",
  ADMIN_CONTENT: "admin-content",
  ADMIN_USERS: "admin-users",
  ADMIN_MARKETING: "admin-marketing",
  ADMIN_SYSTEM: "admin-system",
  UTILITY: "utility",
});

/**
 * All admin section IDs for convenience.
 */
export const ADMIN_SECTIONS = [
  SECTION.ADMIN_ORDERS,
  SECTION.ADMIN_CONTENT,
  SECTION.ADMIN_USERS,
  SECTION.ADMIN_MARKETING,
  SECTION.ADMIN_SYSTEM,
];

/**
 * Utility section IDs (FAQ, Log Out) — rendered last.
 */
export const UTILITY_SECTIONS = [SECTION.UTILITY];

/**
 * Icon component map — keyed by name used in NAV_LINKS.
 */
const ICON_MAP = {
  // Shop Dashboard — themeV2 inline SVG icons
  MyDashboardIcon: SpaceDashboardIcon,
  ShopBuilderIcon: StorefrontIcon,
  SalesIcon: ListAltIcon,
  ManageEventsIcon: EventIcon,
  ReturnKitsIcon: LocalShippingIcon,
  GetPaidIcon: PaymentsIcon,
  ReferralIcon: TrendingUpIcon,
  AccountSettingsIcon: SettingsIcon,
  // Shop Dashboard — still MUI v4 (TC/agent/admin-specific)
  ViewCompactOutlined: ViewCompactOutlinedIcon,
  ReportingIcon: Icons.ReportingIcon,
  AnalyticsIcon: Icons.AnalyticsIcon,
  // User Account — MUI v4
  DashboardRounded: DashboardRoundedIcon,
  ListOutlined: ListOutlinedIcon,
  StarsIcon: StarsIconMui,
  FavoriteBorder: FavoriteBorderIcon,
  NotificationsOutlined: NotificationsOutlinedIcon,
  LockOpenOutlined: LockOpenOutlinedIcon,
  LiveHelpOutlined: LiveHelpOutlinedIcon,
  ExitToAppOutlined: ExitToAppOutlinedIcon,
  // Admin — MUI v4
  SearchOutlined: SearchOutlinedIcon,
  CheckmarkIcon: CheckmarkIconImg,
  LocalShipping: LocalShippingIconMui,
  AccountBalanceWalletOutlined: AccountBalanceWalletOutlinedIcon,
  AssignmentTurnedInOutlined: AssignmentTurnedInOutlinedIcon,
  FindInPageOutlined: FindInPageOutlinedIcon,
  CategoryIcon: CategoryIconMui,
  TheatersIcon: TheatersIconMui,
  AccountBoxIcon: AccountBoxIconMui,
  EmojiPeople: EmojiPeopleIcon,
  FormatListNumberedRtl: FormatListNumberedRtlIcon,
  LinkOutlined: LinkOutlinedIcon,
  AssignmentIndOutlined: AssignmentIndOutlinedIcon,
  CalendarToday: CalendarTodayIcon,
  AllInboxIcon: AllInboxIconMui,
  ReceiptIcon: ReceiptIconMui,
  MoreOutlined: MoreOutlinedIcon,
  ViewComfy: ViewComfyIcon,
  QueueIcon: QueueIconMui,
  InsertChart: InsertChartIcon,
  ShopOutlined: ShopOutlinedIcon,
  StyleIcon: StyleIconMui,
  PublishIcon: PublishIconMui,
  SettingsOutlined: SettingsOutlinedIcon,
};

/**
 * Resolves an icon name to its component.
 */
export const getIconComponent = (name) => ICON_MAP[name] ?? null;

// ---------------------------------------------------------------------------
// Guard helpers
// ---------------------------------------------------------------------------

const isStandardShop = ({ activeInfluencer: ai }) =>
  Boolean(ai) && !ai.hasNoShopEditor && !ai.isAgentShop && !ai.isTcShop;

const isNotAgentOrTc = ({ activeInfluencer: ai }) =>
  Boolean(ai) && !ai.isAgentShop && !ai.isTcShop;

const isNotTcShop = ({ activeInfluencer: ai }) => Boolean(ai) && !ai.isTcShop;

const isNotGroupAgentOrTc = ({ activeInfluencer: ai }) =>
  Boolean(ai) && !ai.isGroupShop && !ai.isAgentShop && !ai.isTcShop;

const isTcShop = ({ activeInfluencer: ai }) => Boolean(ai) && ai.isTcShop;

const isAgentShop = ({ activeInfluencer: ai }) => Boolean(ai) && ai.isAgentShop;

const isAdmin = ({ user }) => user.isAdmin || user.isSuperadmin;

const isSuperadmin = ({ user }) => Boolean(user.isSuperadmin);

const isAdminWithInfluencer = ({ user, activeInfluencer: ai }) =>
  (user.isAdmin || user.isSuperadmin) && Boolean(ai);

const isAdminAgentOrTcShop = ({ user, activeInfluencer: ai }) =>
  (user.isAdmin || user.isSuperadmin) && (ai?.isAgentShop || ai?.isTcShop);

const myDashboardGuard = (ctx) =>
  isStandardShop(ctx) || isAdminAgentOrTcShop(ctx);

const shopBuilderGuard = (ctx) => {
  const { activeInfluencer: ai, user } = ctx;
  return (
    (Boolean(ai) && !ai.hasNoShopEditor && !ai.isAgentShop && !ai.isTcShop) ||
    ((user.isAdmin || user.isSuperadmin) && !ai?.isAgentShop && !ai?.isTcShop)
  );
};

const isAffiliate = ({ user }) => Boolean(user.isAffiliate);

const always = () => true;

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

const shopPath =
  (base) =>
  ({ activeInfluencer: ai }) =>
    ai ? `${base}/${ai.route}` : "/Dashboard/";

const eventCalendarPath = () =>
  `/account-dashboard/event-calendar/month/${format(new Date(), "yyyy-MM-dd")}`;

// ---------------------------------------------------------------------------
// NAV_LINKS — the single source of truth for all navigation links
// ---------------------------------------------------------------------------

export const NAV_LINKS = [
  // =========================================================================
  // Shop Dashboard links
  // =========================================================================
  {
    id: "my-dashboard",
    label: "My Dashboard",
    iconName: "MyDashboardIcon",
    path: shopPath("/Dashboard/MyDashboard"),
    section: SECTION.SHOP_DASHBOARD,
    exact: true,
    guard: myDashboardGuard,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "shop-builder",
    label: "Shop Builder",
    iconName: "ShopBuilderIcon",
    path: shopPath("/Dashboard/s"),
    section: SECTION.SHOP_DASHBOARD,
    guard: shopBuilderGuard,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "tc-portal",
    label: "TC Portal",
    iconName: "ViewCompactOutlined",
    path: "/Dashboard/TalentCoordinatorPortal",
    section: SECTION.SHOP_DASHBOARD,
    guard: isTcShop,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "tc-reporting",
    label: "Reporting",
    iconName: "ReportingIcon",
    path: "/Dashboard/TalentCoordinatorReporting",
    section: SECTION.SHOP_DASHBOARD,
    guard: (ctx) => isTcShop(ctx) && featureFlags.AGENT_REPORTING_ENABLED,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "agent-overview",
    label: "Agent Overview",
    iconName: "ViewCompactOutlined",
    path: "/Dashboard/AgentOverview",
    section: SECTION.SHOP_DASHBOARD,
    guard: isAgentShop,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "agent-reporting",
    label: "Reporting",
    iconName: "ReportingIcon",
    path: "/Dashboard/AgentReporting",
    section: SECTION.SHOP_DASHBOARD,
    guard: (ctx) => isAgentShop(ctx) && featureFlags.AGENT_REPORTING_ENABLED,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "sales-overview",
    label: "Sales Overview",
    iconName: "SalesIcon",
    path: shopPath("/Dashboard/SalesOverview"),
    section: SECTION.SHOP_DASHBOARD,
    exact: true,
    guard: isNotGroupAgentOrTc,
    badge: "unsignedCount",
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "manage-events",
    label: "Manage Events",
    iconName: "ManageEventsIcon",
    path: "/Dashboard/Manage-Events",
    section: SECTION.SHOP_DASHBOARD,
    exact: true,
    guard: isNotAgentOrTc,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "return-label",
    label: "Return Label",
    iconName: "ReturnKitsIcon",
    path: "/Dashboard/ReturnLabel",
    section: SECTION.SHOP_DASHBOARD,
    exact: true,
    guard: isNotAgentOrTc,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "get-paid",
    label: "Get Paid",
    iconName: "GetPaidIcon",
    path: "/Dashboard/Payout",
    section: SECTION.SHOP_DASHBOARD,
    guard: isNotTcShop,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "make-more",
    label: "Make More $",
    iconName: "ReferralIcon",
    path: "/Dashboard/Referral",
    section: SECTION.SHOP_DASHBOARD,
    guard: isNotAgentOrTc,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "account-settings",
    label: "Account Settings",
    iconName: "AccountSettingsIcon",
    path: "/Dashboard/Account-Settings",
    section: SECTION.SHOP_DASHBOARD,
    guard: isNotTcShop,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },
  {
    id: "admin-analytics-dashboard",
    label: "Admin Analytics",
    iconName: "AnalyticsIcon",
    path: "/Dashboard/AdminAnalytics",
    section: SECTION.SHOP_DASHBOARD,
    exact: true,
    guard: isAdminWithInfluencer,
    contexts: ["dashboard", "account-sidebar", "mobile"],
  },

  // =========================================================================
  // User Account links
  // =========================================================================
  {
    id: "affiliate-portal",
    label: "Affiliate Portal",
    iconName: "DashboardRounded",
    path: "/account-dashboard/affiliate",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: isAffiliate,
    contexts: ["account-menu"],
  },
  {
    id: "my-orders",
    label: "My Orders",
    iconName: "ListOutlined",
    path: "/account-dashboard/orders",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "wishlist",
    label: "Wishlist",
    iconName: "StarsIcon",
    path: "/account-dashboard/wishlist",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "my-favorites",
    label: "My Favorites",
    iconName: "FavoriteBorder",
    path: "/account-dashboard/my-favorites",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "notifications",
    label: "Notifications",
    iconName: "NotificationsOutlined",
    path: "/account-dashboard/notifications",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "change-password",
    label: "Change Password",
    iconName: "LockOpenOutlined",
    path: "/account-dashboard/change-password",
    section: SECTION.USER_ACCOUNT,
    exact: true,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "faq",
    label: "FAQ",
    iconName: "LiveHelpOutlined",
    path: "/FAQ",
    href: true,
    section: SECTION.UTILITY,
    guard: always,
    contexts: ["account-menu"],
  },
  {
    id: "log-out",
    label: "Log Out",
    iconName: "ExitToAppOutlined",
    path: "/logout",
    section: SECTION.UTILITY,
    guard: always,
    contexts: ["account-menu"],
  },

  // =========================================================================
  // Admin — Orders & Fulfillment
  // =========================================================================
  {
    id: "search-orders",
    label: "Search Orders",
    iconName: "SearchOutlined",
    path: "/account-dashboard/admin",
    section: SECTION.ADMIN_ORDERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "shipping-assistant",
    label: "Shipping Assistant",
    iconName: "CheckmarkIcon",
    path: "/account-dashboard/shippingAssistant",
    section: SECTION.ADMIN_ORDERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "print-boxes",
    label: "Print Boxes",
    iconName: "LocalShipping",
    path: "/account-dashboard/print-boxes",
    section: SECTION.ADMIN_ORDERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "payouts",
    label: "Payouts",
    iconName: "AccountBalanceWalletOutlined",
    path: "/account-dashboard/payouts",
    section: SECTION.ADMIN_ORDERS,
    guard: isSuperadmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },

  // =========================================================================
  // Admin — Content & Products
  // =========================================================================
  {
    id: "product-approval",
    label: "Product Approval",
    iconName: "AssignmentTurnedInOutlined",
    path: "/account-dashboard/productApproval",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "product-search",
    label: "Product Search",
    iconName: "FindInPageOutlined",
    path: "/account-dashboard/products",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "image-approval",
    label: "Image Approval",
    iconName: "FindInPageOutlined",
    path: "/account-dashboard/imageApproval",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "product-type-assignment",
    label: "Product Type Assignment",
    iconName: "AssignmentTurnedInOutlined",
    path: "/account-dashboard/productTypes",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "categories",
    label: "Categories",
    iconName: "CategoryIcon",
    path: "/account-dashboard/categories",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "franchises",
    label: "Franchises",
    iconName: "TheatersIcon",
    path: "/account-dashboard/franchises",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "wishlisted-products",
    label: "Wishlisted Products",
    iconName: "StarsIcon",
    path: "/account-dashboard/wishlisted-products",
    section: SECTION.ADMIN_CONTENT,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },

  // =========================================================================
  // Admin — Users & Accounts
  // =========================================================================
  {
    id: "account-approval",
    label: "Account Approval",
    iconName: "AccountBoxIcon",
    path: "/account-dashboard/accountApproval",
    section: SECTION.ADMIN_USERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "users-search",
    label: "Users Search",
    iconName: "EmojiPeople",
    path: "/account-dashboard/users",
    section: SECTION.ADMIN_USERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "user-actions",
    label: "User Actions",
    iconName: "FormatListNumberedRtl",
    path: "/account-dashboard/userActions",
    section: SECTION.ADMIN_USERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "access-links",
    label: "Access Links",
    iconName: "LinkOutlined",
    path: "/account-dashboard/accessLinks",
    section: SECTION.ADMIN_USERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "talent-coordinators",
    label: "Talent Coordinators",
    iconName: "AssignmentIndOutlined",
    path: "/account-dashboard/talent-coordinators",
    section: SECTION.ADMIN_USERS,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },

  // =========================================================================
  // Admin — Marketing & Events
  // =========================================================================
  {
    id: "event-calendar",
    label: "Event Calendar",
    iconName: "CalendarToday",
    path: eventCalendarPath,
    section: SECTION.ADMIN_MARKETING,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "promotions",
    label: "Promotions",
    iconName: "AllInboxIcon",
    path: "/account-dashboard/promotions",
    section: SECTION.ADMIN_MARKETING,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "promo-code-manager",
    label: "Promo Code Manager",
    iconName: "ReceiptIcon",
    path: "/account-dashboard/promoCodeManager",
    section: SECTION.ADMIN_MARKETING,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "reports",
    label: "Reports",
    iconName: "ListOutlined",
    path: "/account-dashboard/reports",
    section: SECTION.ADMIN_MARKETING,
    guard: (ctx) => isAdmin(ctx) && featureFlags.REPORTS_ENABLED,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "waitlists",
    label: "Waitlists",
    iconName: "QueueIcon",
    path: "/account-dashboard/waitlists",
    section: SECTION.ADMIN_MARKETING,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },

  // =========================================================================
  // Admin — System & Config
  // =========================================================================
  {
    id: "admin-query",
    label: "Admin Query",
    iconName: "ViewComfy",
    path: "/account-dashboard/admin-query",
    section: SECTION.ADMIN_SYSTEM,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "site-configuration",
    label: "Site Configuration",
    iconName: "SettingsOutlined",
    path: "/account-dashboard/site-configuration",
    section: SECTION.ADMIN_SYSTEM,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "tag-approval",
    label: "Tag Approval",
    iconName: "MoreOutlined",
    path: "/account-dashboard/tag-approval",
    section: SECTION.ADMIN_SYSTEM,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "tag-manager",
    label: "Tag Manager",
    iconName: "MoreOutlined",
    path: "/account-dashboard/tag-manager",
    section: SECTION.ADMIN_SYSTEM,
    guard: isAdmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "analytics",
    label: "Analytics",
    iconName: "InsertChart",
    path: "/account-dashboard/analytics",
    section: SECTION.ADMIN_SYSTEM,
    guard: isSuperadmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "autopay-shops",
    label: "Autopay Shops",
    iconName: "ShopOutlined",
    path: "/account-dashboard/autopay-list",
    section: SECTION.ADMIN_SYSTEM,
    guard: isSuperadmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "verticals",
    label: "Verticals",
    iconName: "StyleIcon",
    path: "/account-dashboard/verticals",
    section: SECTION.ADMIN_SYSTEM,
    guard: isSuperadmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
  {
    id: "upload-orders",
    label: "Upload Orders",
    iconName: "PublishIcon",
    path: "/account-dashboard/upload-orders",
    section: SECTION.ADMIN_SYSTEM,
    guard: isSuperadmin,
    contexts: ["dashboard", "mobile", "account-sidebar"],
  },
];
